import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useDigcompAIMapping,
  type DigcompEntityType,
  type DigcompMappingContent,
  type DigcompMappingResult,
  type DigcompCourseMappingResult,
} from '@/hooks/useDigcompAIMapping';

export interface DigcompAIMapButtonProps {
  entityType: DigcompEntityType;
  entityId?: string | null;
  brandId: string | null;
  content: DigcompMappingContent;
  onSuccess?: (result: DigcompMappingResult | DigcompCourseMappingResult | null) => void | Promise<void>;
  disabled?: boolean;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

/**
 * Button that triggers AI-powered DigComp mapping.
 * Calls the LLM to suggest competence/level mapping, then invokes onSuccess with the result.
 * The parent is responsible for persisting the mapping to the entity.
 */
export function DigcompAIMapButton({
  entityType,
  entityId,
  brandId,
  content,
  onSuccess,
  disabled = false,
  variant = 'outline',
  size = 'sm',
  className,
}: DigcompAIMapButtonProps) {
  const { mutate, isPending } = useDigcompAIMapping({
    brandId,
    entityType,
    entityId,
    content,
  });

  const hasContent = Object.values(content).some(
    (v) => v !== undefined && v !== null && v !== ''
  );

  const handleClick = () => {
    if (!brandId) {
      toast.error('No brand selected');
      return;
    }
    if (!hasContent) {
      toast.error('Add some content (title, description, etc.) before mapping');
      return;
    }

    mutate(undefined, {
      onSuccess: async (result) => {
        if (result) {
          if ('competences' in result) {
            if (result.competences.length > 0) {
              toast.success(`AI suggested ${result.competences.length} competence(s)`);
              await onSuccess?.(result);
            } else {
              toast.info('AI found no matching competences');
            }
          } else {
            toast.success('AI mapping applied');
            await onSuccess?.(result);
          }
        } else {
          toast.info('AI found no matching competence');
        }
      },
      onError: (error: Error) => {
        toast.error(error.message || 'Failed to map with AI');
      },
    });
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size={size}
      disabled={disabled || isPending || !brandId || !hasContent}
      onClick={handleClick}
      className={`btn-collab-gradient border-2 border-emerald-500/80 text-white hover:bg-transparent hover:text-white hover:opacity-95 ${className ?? ''}`}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Sparkles className="h-4 w-4" />
      )}
      <span className="ml-2">{isPending ? 'Mapping...' : 'Map with AI'}</span>
    </Button>
  );
}
